import React, { useState } from 'react';
import { Button, DatePicker, Form, Input, InputNumber, Modal, Space, Table, Tooltip, Typography, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useGetContractsByClientIdQuery,
  useCreateContractMutation,
  type ContractRow,
} from '../../store/api/referencesApi';

const { Text } = Typography;

type Props = {
  clientId: number;
  canAdd: boolean;
};

const ContractorContractsPanel: React.FC<Props> = ({ clientId, canAdd }) => {
  const { data: rows = [], isLoading, refetch } = useGetContractsByClientIdQuery(clientId);
  const [createContract, { isLoading: creating }] = useCreateContractMutation();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const columns = [
    { title: 'Номер', dataIndex: 'number', key: 'number' },
    {
      title: 'Дата',
      dataIndex: 'date',
      key: 'date',
      render: (d: string) => (d ? dayjs(d).format('DD.MM.YYYY') : '—'),
    },
    {
      title: 'Предоплата, %',
      dataIndex: 'prepaymentPercent',
      key: 'prepaymentPercent',
      render: (v: number | null) => (v != null ? String(v) : '—'),
    },
    {
      title: 'Отсрочка предоплаты, дн.',
      dataIndex: 'prepaymentDelayDays',
      key: 'prepaymentDelayDays',
      render: (v: number | null) => (v != null ? String(v) : '—'),
    },
    {
      title: 'Доплата, %',
      dataIndex: 'postpaymentPercent',
      key: 'postpaymentPercent',
      render: (v: number | null) => (v != null ? String(v) : '—'),
    },
    {
      title: 'Отсрочка доплаты, дн.',
      dataIndex: 'postpaymentDelayDays',
      key: 'postpaymentDelayDays',
      render: (v: number | null) => (v != null ? String(v) : '—'),
    },
    {
      title: 'Условия оплаты (текст)',
      dataIndex: 'paymentTermsText',
      key: 'paymentTermsText',
      width: 320,
      render: (t: string | null | undefined) =>
        t != null && String(t).trim() !== '' ? (
          <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{t}</span>}>
            <Text ellipsis style={{ maxWidth: 300, display: 'inline-block' }}>
              {t}
            </Text>
          </Tooltip>
        ) : (
          '—'
        ),
    },
  ];

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await createContract({
        clientId,
        number: String(values.number).trim(),
        date: (values.date as dayjs.Dayjs).format('YYYY-MM-DD'),
        prepaymentPercent: values.prepaymentPercent ?? null,
        prepaymentDelayDays: values.prepaymentDelayDays ?? null,
        postpaymentPercent: values.postpaymentPercent ?? null,
        postpaymentDelayDays: values.postpaymentDelayDays ?? null,
        paymentTermsText:
          values.paymentTermsText != null && String(values.paymentTermsText).trim() !== ''
            ? String(values.paymentTermsText).trim()
            : null,
      }).unwrap();
      message.success('Договор создан');
      form.resetFields();
      setOpen(false);
      void refetch();
    } catch (e: unknown) {
      if (e && typeof e === 'object' && 'errorFields' in e) return;
      const msg = (e as { data?: { message?: string } })?.data?.message;
      message.error(msg || 'Не удалось создать договор');
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {canAdd ? (
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
          + Добавить договор
        </Button>
      ) : null}

      <Table<ContractRow>
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={rows}
        pagination={rows.length > 10 ? { pageSize: 10 } : false}
        size="small"
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'Нет договоров' }}
      />

      <Modal
        title="Новый договор"
        open={open}
        onCancel={() => {
          setOpen(false);
          form.resetFields();
        }}
        onOk={handleSubmit}
        confirmLoading={creating}
        destroyOnClose
        width={560}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item
            label="Номер договора"
            name="number"
            rules={[{ required: true, message: 'Укажите номер' }]}
          >
            <Input placeholder="Номер" maxLength={100} />
          </Form.Item>
          <Form.Item
            label="Дата договора"
            name="date"
            rules={[{ required: true, message: 'Укажите дату' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item label="Предоплата, %" name="prepaymentPercent">
            <InputNumber min={0} max={100} style={{ width: '100%' }} placeholder="Необязательно" />
          </Form.Item>
          <Form.Item label="Отсрочка предоплаты, дн." name="prepaymentDelayDays">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="Необязательно" />
          </Form.Item>
          <Form.Item label="Доплата, %" name="postpaymentPercent">
            <InputNumber min={0} max={100} style={{ width: '100%' }} placeholder="Необязательно" />
          </Form.Item>
          <Form.Item label="Отсрочка доплаты, дн." name="postpaymentDelayDays">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="Необязательно" />
          </Form.Item>
          <Form.Item label="Условия оплаты (текст)" name="paymentTermsText">
            <Input.TextArea rows={4} placeholder="Необязательно" showCount maxLength={8000} />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Клиент подставляется из карточки автоматически.
          </Text>
        </Form>
      </Modal>
    </Space>
  );
};

export default ContractorContractsPanel;
